/**
 * **알림 규칙 한 줄을 사람 말로** — 카드가 읽는다.
 *
 * 🔑 어휘는 `constants/alert-rule` 한 곳에서만 읽는다 — 이름을 고친 날 두 화면이 갈리지 않게.
 * ⚠️ 저장본이 두 벌씩이라 넓게 받는다(`Record<string, any>`) — 읽는 칸만 좁힌다.
 * ⚠️ 모르는 값은 값 그대로 돌려준다 — 빈칸으로 두면 무엇이 걸려 있는지 알 길이 없다.
 */

import {
  ALERT_SOURCE_OPTIONS,
  ALERT_CONTENT_OPTIONS,
  ALERT_WEEKDAY_OPTIONS,
  ALERT_SCHEDULE_OPTIONS,
  ALERT_CONDITION_OPTIONS,
} from 'src/constants/alert-rule';

function labelOf(rows: readonly { value: string; label: string }[], value: unknown): string {
  const key = String(value ?? '').trim();
  return rows.find((row) => row.value === key)?.label ?? key;
}

/** 무엇을 지켜보나 — 대상 이름(캠페인을 골랐으면 그 이름까지) */
export function alertSourceLabel(rule: Record<string, any> | null | undefined, campaignName?: string): string {
  const base = labelOf(ALERT_SOURCE_OPTIONS, rule?.source?.type);
  const name = String(campaignName ?? '').trim();
  return name ? `${base} · ${name}` : base;
}

/** 언제 보내나 — 매주면 요일까지, 매일·매주면 시각까지 */
export function alertScheduleLabel(rule: Record<string, any> | null | undefined): string {
  const mode = String(rule?.schedule?.mode ?? '').trim();
  const base = labelOf(ALERT_SCHEDULE_OPTIONS, mode);
  if (mode !== 'daily' && mode !== 'weekly') return base;
  const hour = Number(rule?.schedule?.hour);
  const time = Number.isFinite(hour) ? `${String(hour).padStart(2, '0')}시` : '';
  if (mode === 'weekly') {
    const day = ALERT_WEEKDAY_OPTIONS.find((row) => row.value === Number(rule?.schedule?.dayOfWeek))?.label ?? '';
    return [base, day, time].filter(Boolean).join(' ');
  }
  return [base, time].filter(Boolean).join(' ');
}

/**
 * 무엇이 걸리면 보내나.
 * 🔑 **조건이 없으면 「새 글 전부」다** — 빈 목록은 「아무것도 안 온다」가 아니다.
 */
export function alertConditionLabels(rule: Record<string, any> | null | undefined): string[] {
  const rows = Array.isArray(rule?.conditions) ? rule.conditions : [];
  const live = rows.filter((row: any) => String(row?.type ?? '') && String(row?.type) !== 'all_new_posts');
  if (live.length === 0) return ['새 글 전부'];
  return live.map((row: any) => {
    const option = ALERT_CONDITION_OPTIONS.find((one) => one.value === String(row?.type));
    if (!option) return String(row?.type);
    const value = Number(row?.params?.[option.paramKey]);
    if (!Number.isFinite(value)) return option.label;
    return `${option.label} ${value.toLocaleString('ko-KR')}${option.unit === 'percent' ? '%' : ''} 이상`;
  });
}

/** 어디로 보내나 — 켜 둔 것만 */
export function alertChannelLabels(rule: Record<string, any> | null | undefined): string[] {
  const out: string[] = [];
  if (rule?.channels?.slack?.enabled) out.push('Slack');
  const email = rule?.channels?.email;
  if (email?.enabled) {
    const count = Array.isArray(email?.recipientUserIds) ? email.recipientUserIds.length : 0;
    out.push(count > 0 ? `이메일 ${count}명` : '이메일');
  }
  return out;
}

/** 무엇을 실어 보내나 — 켜 둔 항목만 */
export function alertContentLabels(rule: Record<string, any> | null | undefined): string[] {
  const content = (rule?.content ?? {}) as Record<string, unknown>;
  return ALERT_CONTENT_OPTIONS.filter((row) => content[row.value] === true).map((row) => row.label);
}

/**
 * 카드 밑줄 한 줄 — 「대상 · 주기 · 조건」.
 * ⚠️ 받는 곳과 실을 내용은 칩으로 따로 적는다 — 한 줄에 다 넣으면 잘린다.
 */
export function alertRuleLine(rule: Record<string, any> | null | undefined, campaignName?: string): string {
  return [alertSourceLabel(rule, campaignName), alertScheduleLabel(rule), alertConditionLabels(rule).join(' · ')]
    .filter(Boolean)
    .join(' · ');
}
